/**
 * Deciding what an effort checkpoint means on launch.
 *
 * Everything in this module is a *read* plus a decision. Restoring an effort
 * must never mutate or delete the checkpoint it restores from (F-TR-42): if the
 * restore then fails, the bytes are still on the device and the user can try
 * again on the next launch. The only function here that writes is the explicit
 * discard the user has to confirm.
 */

import type {
  EffortCheckpoint,
  EffortOutcome,
  FailClosedTrigger,
  OpenEffortCheckpoint,
  StructuredEffortCheckpoint,
  WriteOutcome,
} from "@ff/domain";
import type { CheckpointStorePort, EffortCheckpointKind } from "@ff/domain/ports";
import {
  EFFORT_FAILURE_COPY,
  UNREADABLE_CHECKPOINT_COPY,
  unreadableCheckpointFailure,
} from "@ff/domain";

/**
 * A restorable effort.
 *
 * `autoStart` is deliberately not a field. Neither shape ever resumes running:
 * a session that starts ticking because the app launched would bank time the
 * user was not training, so the restored effort is always paused and the person
 * decides when it continues.
 */
export type ResumableEffort =
  | { readonly shape: "duration"; readonly checkpoint: OpenEffortCheckpoint }
  | { readonly shape: "structured"; readonly checkpoint: StructuredEffortCheckpoint };

export type ResumeDecision =
  /** Nothing saved on this device. */
  | { readonly kind: "none" }
  | { readonly kind: "resume"; readonly effort: ResumableEffort }
  /**
   * A finished checkpoint whose archive never confirmed. Not offered as a
   * resume — the training is over — but as an idempotent retry of the archive,
   * with the end instant the checkpoint froze.
   */
  | {
      readonly kind: "pendingArchive";
      readonly checkpoint: EffortCheckpoint;
      readonly terminalOutcome: EffortOutcome;
    }
  /**
   * The bytes are present and untrustworthy. The app must not guess at them and
   * must not quietly delete them; it asks, with {@link UNREADABLE_CHECKPOINT_COPY}.
   */
  | {
      readonly kind: "unreadable";
      readonly shape: EffortCheckpointKind;
      readonly reason: FailClosedTrigger;
      readonly detail: string;
      readonly confirmation: typeof UNREADABLE_CHECKPOINT_COPY;
    };

/**
 * Reads both checkpoint stores and decides what the launch should offer.
 *
 * Open effort is preferred over the structured snapshot. The two stores are
 * singletons and cannot both hold a live effort by design, so a pair is already
 * an anomaly; preferring the count-up is the safer answer because it is the
 * shape whose duration cannot be reconstructed from a plan.
 *
 * An unreadable read short-circuits the other store: an untrustworthy byte
 * region is a fact about the device that the user has to be told about before
 * anything else is started on top of it.
 */
export async function decideResume(
  checkpointStore: Pick<CheckpointStorePort, "readOpenEffort" | "readStructured">,
): Promise<ResumeDecision> {
  const open = await checkpointStore.readOpenEffort();
  if (open.status === "unreadable") {
    return {
      kind: "unreadable",
      shape: "duration",
      reason: open.trigger,
      detail: open.detail,
      confirmation: UNREADABLE_CHECKPOINT_COPY,
    };
  }
  if (open.status === "loaded") {
    const terminal = open.value.terminalSession;
    if (terminal !== undefined) {
      return {
        kind: "pendingArchive",
        checkpoint: open.value,
        terminalOutcome: terminal.outcome,
      };
    }
    return { kind: "resume", effort: { shape: "duration", checkpoint: open.value } };
  }

  const structured = await checkpointStore.readStructured();
  if (structured.status === "unreadable") {
    return {
      kind: "unreadable",
      shape: "structured",
      reason: structured.trigger,
      detail: structured.detail,
      confirmation: UNREADABLE_CHECKPOINT_COPY,
    };
  }
  if (structured.status === "loaded") {
    const terminal = structured.value.terminalOutcome;
    if (terminal !== undefined) {
      return { kind: "pendingArchive", checkpoint: structured.value, terminalOutcome: terminal };
    }
    return { kind: "resume", effort: { shape: "structured", checkpoint: structured.value } };
  }

  return { kind: "none" };
}

/** The copy shown when a restore that read cleanly still could not be opened. */
export const RESTORE_FAILURE_MESSAGE = EFFORT_FAILURE_COPY.restore;

export type DiscardUnreadableResult =
  | { readonly kind: "discarded" }
  | { readonly kind: "failed"; readonly outcome: WriteOutcome; readonly message: string };

/**
 * Removes an unreadable checkpoint, after the user has confirmed it.
 *
 * The failure message names the device rather than saying "your phone", because
 * the same build runs on a phone, a tablet and a desktop and the sentence has to
 * stay true on all three.
 */
export async function discardUnreadableCheckpoint(
  shape: EffortCheckpointKind,
  deviceNoun: string,
  checkpointStore: Pick<CheckpointStorePort, "clear">,
): Promise<DiscardUnreadableResult> {
  const cleared = await checkpointStore.clear(shape);
  if (cleared.status !== "verified") {
    return {
      kind: "failed",
      outcome: cleared,
      message: unreadableCheckpointFailure(deviceNoun),
    };
  }
  return { kind: "discarded" };
}
